import {
  Button,
  Checkbox,
  InfoTooltip,
  FieldMessage,
  CollapsibleSection,
  DocumentUploadArea,
  ExtractionStatus,
  GenerationStatus,
  ImageUploadArea,
  LiveRegion,
  RadioGroup,
  RadioGroupItem,
  Select,
  useTranslate,
  type SelectOption,
} from '@athena/ui'
import { useQuery } from '@apollo/client/react'
import { LIMITS, QuestionType } from '@athena/shared'
import { useState } from 'react'

import { SubscriptionUsageDocument } from '../../graphql/generated/operations.js'
import { countOf } from '../../lib/format.js'

import { ImageSizeReport } from './ImageSizeReport.js'
import { MAX_IMAGE_MEGABYTES, MAX_IMAGES } from './imageSelection.js'
import { MAX_PDF_MEGABYTES, MAX_PDFS } from './pdfSelection.js'
import { sizesForPlan, questionsForPlan, type PracticeSize } from './practiceSize.js'
import type { AutomaticPractice } from './useAutomaticPractice.js'

export interface AutomaticPracticeFormProps {
  /** The catalog's subjects, localized by the API. Never a list from here. */
  subjectOptions: SelectOption[]
  /** The tab's state, from `useAutomaticPractice` in the screen above. */
  automatic: AutomaticPractice
}

/**
 * Creating a practice from photographs of it.
 *
 * Images in, a practice out. Every photograph is compressed in the browser and
 * the whole set goes to Athena's own API in one request, which transcribes
 * them into one document and then writes a practice from it. One button runs
 * the whole chain, because from where the tutor sits it is one thing they
 * asked for.
 *
 * No per-tile retry, and that follows from the batch: the pages are read
 * together, so a failure is the reading rather than one photograph. A tutor
 * whose third page came out badly removes it and reads again.
 *
 * A valid generated document is persisted by the API as a draft and the
 * parent screen immediately opens its canonical practice page.
 *
 * The subject is asked for because it is what the practice is told it is about,
 * and because a tutor who has not chosen one has not finished describing what
 * they want. It comes from the same catalog query the manual tab uses; there is
 * one catalog — and it is asked for once, at the top, so the practice is ready
 * to create the moment the tutor is happy with it. Education level is not asked
 * for anywhere in this flow.
 *
 * Size and question type have defaults. There used to
 * be three — a topic, a number of questions, and a box for anything else — and
 * every one of them was optional, which is a form saying "here are three things
 * you could think about" to somebody who came to upload a worksheet. The topic
 * is whatever the pages are about, and the model reads the pages.
 *
 * ── Why the configuration folds away ─────────────────────────────────────
 *
 * The whole of it — subject, size, uploader and pages — is one collapsible
 * section so a tutor can keep the setup compact before running it.
 *
 * Models remain a server decision. A provider choice appears only when the
 * server publishes the development override capability; hidden UI is never
 * treated as the enforcement for it.
 *
 * The state is a prop rather than this component's own, so that a look at the
 * Manual tab does not discard a tutor's images. `useAutomaticPractice` says
 * more about why.
 */
export function AutomaticPracticeForm({ subjectOptions, automatic }: AutomaticPracticeFormProps) {
  const t = useTranslate()
  const subjectAvailable = subjectOptions.some((option) => option.value === automatic.subjectId)

  /** Whether the tutor has the setup disclosure open. */
  const [configOpen, setConfigOpen] = useState(true)

  /**
   * How many questions this reader's plan holds in one practice.
   *
   * Read from the subscription rather than assumed, because the sizes below
   * are shown with the number they will actually produce: a tutor on Free
   * sees one fifteen-question option instead of four equivalent presets.
   *
   * `MAX_QUESTIONS` while the query is in flight, so the labels start at the
   * preset's own number and settle rather than starting clamped and jumping
   * up. The server clamps either way.
   */
  const usage = useQuery(SubscriptionUsageDocument)
  const questionLimit = usage.data?.mySubscription.questionLimit ?? LIMITS.MAX_QUESTIONS
  const aiRemaining = usage.data?.mySubscription.ai.remaining ?? null

  /**
   * The sizes, each showing what it actually means.
   *
   * "M" alone is a guess and "50" alone is not a decision, so the option is
   * both. The count comes from `questionsForPlan`, which is the only place the
   * numbers are written down, and the words for it come from the same
   * `count.questions…` pair every other count in the product uses.
   */
  const sizes = sizesForPlan(questionLimit)
  const selectedSize =
    sizes.find(
      (size) =>
        questionsForPlan(size, questionLimit) === questionsForPlan(automatic.size, questionLimit),
    ) ?? automatic.size
  const sizeOptions: SelectOption[] = sizes.map((size) => ({
    value: size,
    // ` · ` is punctuation, the same separator as every other meta line here.
    label: `${t(`practice.automatic.sizeLabel.${size}`)} · ${countOf(t, 'questions', questionsForPlan(size, questionLimit))}`,
  }))

  const configuration = (
    <div className="grid min-w-0 gap-6 md:gap-8 desktop:grid-cols-[minmax(16rem,1fr)_minmax(0,2fr)] desktop:items-stretch">
      <div className="flex min-w-0 flex-col gap-6">
        {/* The controls form the supporting column once there is enough room
            for the upload target to sit beside them. At smaller widths they
            remain first in the same document order. */}
        <div className="grid gap-4 md:grid-cols-2 desktop:grid-cols-1">
          <Select
            label={t('practice.fields.subject')}
            placeholder={t('practice.fields.chooseSubject')}
            options={subjectOptions}
            searchable
            visibleOptionCount={6}
            value={subjectAvailable ? automatic.subjectId : ''}
            onValueChange={automatic.setSubjectId}
            disabled={automatic.running}
          />
          <Select
            label={t('practice.automatic.size')}
            options={sizeOptions}
            value={selectedSize}
            onValueChange={(value) => automatic.setSize(value as PracticeSize)}
            disabled={automatic.running}
          />
        </div>

        <fieldset
          className="flex min-w-0 flex-col gap-2"
          aria-describedby={
            automatic.questionTypes.length === 0 ? 'question-types-error' : undefined
          }
          aria-invalid={automatic.questionTypes.length === 0}
        >
          <legend className="mb-1 text-label font-semibold text-text-strong">
            <span className="inline-flex items-center gap-1">
              {t('practice.automatic.questionTypes')}
              {automatic.allowedQuestionTypes.length === 1 ? (
                <InfoTooltip
                  content={t('practice.automatic.questionTypesFreeHint')}
                  label={t('practice.automatic.questionTypesInfo')}
                />
              ) : null}
            </span>
          </legend>
          <div className="grid grid-cols-2 gap-0 desktop:grid-cols-1">
            {Object.values(QuestionType).map((type) => {
              const available = automatic.allowedQuestionTypes.includes(type)
              const checked = automatic.questionTypes.includes(type)
              return (
                <div key={type}>
                  <Checkbox
                    density="compact"
                    checked={checked}
                    disabled={automatic.running || !available}
                    label={
                      <span
                        className={`text-body-sm ${available ? 'text-text' : 'text-text-disabled'}`}
                      >
                        {t(`questionType.${type}`)}
                      </span>
                    }
                    onCheckedChange={(value) =>
                      automatic.setQuestionTypes(
                        value === true
                          ? [...automatic.questionTypes, type]
                          : automatic.questionTypes.filter((selected) => selected !== type),
                      )
                    }
                  />
                </div>
              )
            })}
          </div>
          {automatic.questionTypes.length === 0 ? (
            <div id="question-types-error">
              <FieldMessage tone="error">
                {t('practice.automatic.questionTypesRequired')}
              </FieldMessage>
            </div>
          ) : null}
        </fieldset>

        {automatic.providerOverrideEnabled ? (
          <Select
            label={t('practice.automatic.aiProvider')}
            options={[
              { value: 'GEMINI', label: t('practice.automatic.providerGemini') },
              { value: 'OPENAI', label: t('practice.automatic.providerOpenAI') },
            ]}
            value={automatic.provider}
            onValueChange={(value) =>
              automatic.setProvider(value === 'OPENAI' ? 'OPENAI' : 'GEMINI')
            }
            disabled={automatic.running}
          />
        ) : null}
      </div>

      {/* The chosen material is the primary working surface. Upload component
          ownership and all file state remain unchanged. */}
      <div className="flex min-w-0 flex-col gap-4">
        {/* Once files are selected, changing source kind requires clearing
            them. The server also refuses mixed image/PDF requests. */}
        {automatic.sourceLocked ? (
          <p className="text-body-sm text-text-secondary">
            {automatic.sourceMode === 'pdf'
              ? t('practice.automatic.sourceLockedPdfs')
              : t('practice.automatic.sourceLockedImages')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <h3 id="source-kind" className="text-label font-semibold text-text-strong">
              {t('practice.automatic.sourceHeading')}
            </h3>
            <RadioGroup
              value={automatic.sourceMode}
              onValueChange={(value) => automatic.setSourceMode(value === 'pdf' ? 'pdf' : 'image')}
              aria-labelledby="source-kind"
              className="flex flex-wrap gap-x-6 gap-y-2"
            >
              <RadioGroupItem
                value="image"
                label={
                  <span className="text-body-sm text-text">
                    {t('practice.automatic.sourceImages')}
                  </span>
                }
              />
              <RadioGroupItem
                value="pdf"
                label={
                  <span className="text-body-sm text-text">
                    {t('practice.automatic.sourcePdfs')}
                  </span>
                }
              />
            </RadioGroup>
          </div>
        )}
        {automatic.sourceMode === 'pdf' ? (
          <DocumentUploadArea
            fillHeight
            documents={automatic.documents}
            maxDocuments={MAX_PDFS}
            maxMegabytes={MAX_PDF_MEGABYTES}
            disabled={automatic.running}
            rejections={automatic.rejections}
            onFiles={automatic.addDocuments}
            onRemove={automatic.removeDocument}
            onClear={automatic.clearDocuments}
          />
        ) : (
          <div className="flex flex-1 flex-col gap-4">
            <ImageUploadArea
              fillHeight
              images={automatic.images}
              maxImages={MAX_IMAGES}
              maxMegabytes={MAX_IMAGE_MEGABYTES}
              disabled={automatic.running}
              rejections={automatic.rejections}
              onFiles={automatic.addImages}
              onRemove={automatic.removeImage}
              onClear={automatic.clearImages}
            />

            {/* TEMPORARY diagnostic. See `ImageSizeReport` for what removing it
                involves. Images only: a PDF is sent as it is, so there is no
                compression to report on. Developers only: to a tutor it is a
                table of numbers about a step they never see. */}
            {automatic.localDevelopmentEnabled ? <ImageSizeReport sizes={automatic.sizes} /> : null}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col gap-6">
      {/* Everything the run is configured from, in one reusable disclosure. */}
      <CollapsibleSection
        title={t('practice.automatic.configHeading')}
        open={configOpen}
        onOpenChange={setConfigOpen}
      >
        {configuration}
      </CollapsibleSection>

      {/* The transcript is a developer's check on the reading, not something
          a tutor acts on — the practice below is what they review. */}
      <ExtractionStatus
        state={automatic.extraction}
        showTranscript={automatic.localDevelopmentEnabled}
      />
      {/* Retrying goes straight back to the provider with the pages already
          read. It never re-reads an image, which is what makes a failed
          generation cheap to have another go at. */}
      <GenerationStatus state={automatic.generation} onRetry={() => void automatic.regenerate()} />

      <div className="flex flex-wrap items-center gap-4">
        <Button
          size="lg"
          /* Disabled for the whole chain, not just the half that is running:
             a click landing between reading and generating would otherwise
             start a second run over the same pages. */
          disabled={automatic.incomplete || automatic.running || !subjectAvailable}
          loading={automatic.running}
          loadingText={t('practice.automatic.processing')}
          onClick={() => void automatic.read()}
        >
          {t('practice.automatic.generate')}
        </Button>
        {automatic.incomplete ? (
          <p className="text-body-sm text-text-secondary">
            {t('practice.automatic.needSourceAndSubject')}
          </p>
        ) : null}
        {/* What a generation costs, said before it is spent.
            Photographed pages reach a model and need a credit; the server
            refuses the transcription itself when there is none left, so a
            tutor who reads this has been told before the button rather than
            after it. The Manual tab is named in the exhausted case because it
            is the route that still works. */}
        {aiRemaining === null || automatic.incomplete ? null : aiRemaining === 0 ? (
          <p className="text-body-sm text-text-secondary">{t('subscription.aiExhaustedHint')}</p>
        ) : (
          <p className="text-body-sm text-text-secondary">
            {aiRemaining === 1
              ? t('subscription.aiRemainingHintOne')
              : t('subscription.aiRemainingHintOther', [aiRemaining])}
          </p>
        )}
      </div>

      <LiveRegion message={automatic.announcement} />
    </div>
  )
}
